import { useEffect, useMemo, ReactNode, useState } from "react";

// react-chartjs-2 components
import { Doughnut } from "react-chartjs-2";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";

// DefaultDoughnutChart configurations
import configs from "lib/Charts/DoughnutCharts/DefaultDoughnutChart/configs";

// Declaring props types for DefaultDoughnutChart
interface Props {
  icon?: {
    color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
    component: ReactNode;
  };
  title?: string;
  description?: string | ReactNode;
  textData?: any;
  height?: string | number;
  chart: {
    labels: string[];
    datasets: {
      label: string;
      backgroundColors: string[];
      data: number[];
    };
    cutout?: number;
  };
  [key: string]: any;
}

function DefaultDoughnutChart({
  icon,
  title,
  description,
  height,
  chart,
  textData,
}: Props): JSX.Element {
  const { data, options } = configs(chart.labels || [], chart.datasets || [], chart.cutout);

  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    setChartKey((prevKey) => prevKey + 1);
  }, [textData]);

  const doughnutTextPlugin = {
    id: "doughnutText",
    beforeDraw(chart: any) {
      const width = chart.width;
      const height = chart.height;
      const ctx = chart.ctx;

      const centerX = width / 2;
      const centerY = height / 2 - 35;

      ctx.save();

      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const verticalSpacing = 20;
      const extraSpacing = 10;
      let offset = 0;

      if (textData) {
        for (const key in textData) {
          if (textData.hasOwnProperty(key)) {
            const value = textData[key];

            ctx.font = "bold 20px Roboto";
            ctx.fillStyle = "#344767";
            ctx.fillText(value, centerX, centerY + offset);

            ctx.font = "normal 14px Roboto";
            ctx.fontWeight = 400;
            ctx.fillStyle = "#7B809A";
            ctx.fillText(key, centerX, centerY + offset + verticalSpacing);

            offset += verticalSpacing * 2 + extraSpacing;
          }
        }
      }

      ctx.restore();
    },
  };

  const htmlLegendPlugin = {
    id: "htmlLegend",
    afterUpdate(chart: any) {
      const items = chart.options.plugins.legend.labels.generateLabels(chart);
      const jsLegend = document.getElementById("js-legend");
      jsLegend.innerHTML = "";

      const total = chart.data.datasets[0].data.reduce(
        (sum: number, value: number) => sum + value,
        0
      );

      const ul = document.createElement("ul");
      items.forEach((item: any, index: any) => {
        if (!item.text || item.text.trim() === "") {
          return;
        }

        const percentage = chart?.data?.datasets[0]?.data[index]?.toFixed(2);

        const li = document.createElement("li");
        li.style.display = "flex";
        li.style.marginBottom = "10px";
        li.style.alignItems = "center";

        const boxSpan = document.createElement("span");
        boxSpan.style.background = item.fillStyle;
        boxSpan.style.width = "24px";
        boxSpan.style.height = "24px";
        boxSpan.style.marginRight = "10px";
        boxSpan.style.borderRadius = "8px";

        const textContainer = document.createElement("span");
        textContainer.style.fontSize = "14px";
        textContainer.style.color = "#344767";
        textContainer.style.fontWeight = "700";
        textContainer.style.fontFamily = "Roboto";
        textContainer.textContent = item.text;

        const percentageContainer = document.createElement("span");
        percentageContainer.style.fontSize = "14px";
        percentageContainer.style.color = "grey";
        percentageContainer.style.fontWeight = "400";
        percentageContainer.style.fontFamily = "Roboto";
        percentageContainer.style.marginLeft = "auto";
        percentageContainer.textContent = `${percentage}%`;

        li.appendChild(boxSpan);
        li.appendChild(textContainer);
        li.appendChild(percentageContainer);
        ul.appendChild(li);
      });

      jsLegend.appendChild(ul);
    },
  };

  const renderChart = useMemo(
    () => (
      <MDBox py={2} pr={2} pl={icon.component ? 1 : 2}>
        {title || description ? (
          <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0}>
            {icon.component && (
              <MDBox
                width="4rem"
                height="4rem"
                bgColor={icon.color || "info"}
                variant="gradient"
                coloredShadow={icon.color || "info"}
                borderRadius="xl"
                display="flex"
                justifyContent="center"
                alignItems="center"
                color="white"
                mt={-5}
                mr={2}
              >
                <Icon fontSize="medium">{icon.component}</Icon>
              </MDBox>
            )}
            <MDBox mt={icon.component ? -2 : 0}>
              {title && <MDTypography variant="h6">{title}</MDTypography>}
              <MDBox mb={2}>
                <MDTypography component="div" variant="button" color="text">
                  {description}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        ) : null}
        <MDBox height={height} display="flex">
          <MDBox width={"50%"}>
            <Doughnut
              key={chartKey}
              data={data}
              options={options}
              plugins={[htmlLegendPlugin, doughnutTextPlugin]}
            />
          </MDBox>
          <MDBox display="flex" justifyContent="center" alignItems="center" width={"50%"}>
            <div id="js-legend" style={{ width: "100%", paddingLeft: "18px" }} />
          </MDBox>
        </MDBox>
      </MDBox>
    ),
    [chart, height, textData, chartKey]
  );

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

DefaultDoughnutChart.defaultProps = {
  icon: { color: "info", component: "" },
  title: "",
  description: "",
  height: "19.125rem",
};

export default DefaultDoughnutChart;
